import React, { useEffect, useState, useCallback } from 'react';
import { Container, Card, Table, Button, Spinner, Alert, Nav, Badge, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faPaperclip, faComments } from '@fortawesome/free-solid-svg-icons';
import api from '../api';
import OrderMessages from '../components/OrderMessages';

const euro = (v) => (v == null ? '' : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }));
const dataOra = (v) => (v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '');

// Reclami (incident) aperti dai clienti su IBS/Mirakl. L'API Seller non li elenca direttamente:
// li ricaviamo dagli ordini con reclamo (sincronizzazione ogni 10 minuti). I messaggi sono quelli
// dell'ordine, allegati inclusi.
export default function ReclamiIBS() {
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [stato, setStato] = useState('aperto');
  const [righe, setRighe] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aggiornando, setAggiornando] = useState(false);
  const [error, setError] = useState(null);
  const [selezionato, setSelezionato] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/mirakl/reclami/', { params: { stato } });
      setRighe(res.data || []);
    } catch (e) {
      setError('Impossibile caricare i reclami.');
    } finally {
      setLoading(false);
    }
  }, [stato]);

  useEffect(() => { carica(); }, [carica]);

  const sincronizza = async () => {
    setAggiornando(true);
    try {
      const res = await api.post('/mirakl/reclami/sincronizza');
      toast.success(`Reclami aperti: ${res.data.aperti} (${res.data.nuovi} nuovi, ${res.data.chiusi} chiusi)`);
      await carica();
    } catch (e) {
      toast.error('Sincronizzazione con Mirakl non riuscita');
    } finally {
      setAggiornando(false);
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Reclami IBS</h2>
        <Button variant="outline-secondary" onClick={sincronizza} disabled={aggiornando}>
          {aggiornando ? <Spinner as="span" animation="border" size="sm" /> : <FontAwesomeIcon icon={faSync} className="me-2" />}
          Sincronizza ora
        </Button>
      </div>
      <p className="text-muted small">
        I reclami aperti dai clienti su IBS. Si aggiornano da soli ogni 10 minuti. La data mostrata è l'ultimo aggiornamento
        del reclamo (IBS non comunica quella di apertura). Clicca su una riga per leggere e rispondere ai messaggi, allegati compresi.
      </p>
      <Nav variant="tabs" activeKey={stato} onSelect={(k) => setStato(k)} className="mb-3">
        <Nav.Item><Nav.Link eventKey="aperto">Reclami aperti</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="chiuso">Reclami chiusi</Nav.Link></Nav.Item>
      </Nav>
      {error && <Alert variant="danger">{error}</Alert>}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="p-4 text-center"><Spinner animation="border" /></div>
          ) : righe.length === 0 ? (
            <div className="p-4 text-center text-muted">
              {stato === 'aperto' ? 'Nessun reclamo aperto.' : 'Nessun reclamo chiuso registrato (si tiene traccia dei reclami chiusi da quando sono stati rilevati).'}
            </div>
          ) : isMobile ? (
            <div>
              {righe.map(r => (
                <div key={r.id} className="p-3 border-bottom" style={{ cursor: 'pointer' }} onClick={() => setSelezionato(r)}>
                  <div className="d-flex justify-content-between align-items-start">
                    <strong>{r.motivo || '-'}</strong>
                    <span className="small text-muted text-nowrap ms-2">{dataOra(r.chiuso_at || r.aggiornato_mirakl_at || r.rilevato_at)}</span>
                  </div>
                  <div className="small">
                    {r.mirakl_order_id}
                    {r.data_ordine && <span className="text-muted"> · {new Date(r.data_ordine).toLocaleDateString('it-IT')}</span>}
                    {r.canale && <span className="text-muted"> · {r.canale}</span>}
                  </div>
                  <div className="small">{r.quantita ? `${r.quantita} x ` : ''}{r.prodotto_titolo}</div>
                  <div className="d-flex justify-content-between align-items-center small mt-1">
                    <span className="text-muted">{r.cliente}{r.importo != null && ` · ${euro(r.importo)}`}</span>
                    <span className="text-nowrap">
                      {r.n_messaggi} {r.n_allegati > 0 && <span className="text-muted"><FontAwesomeIcon icon={faPaperclip} /> {r.n_allegati}</span>}
                      {r.da_rispondere && stato === 'aperto' && <Badge bg="warning" text="dark" className="ms-2">Da rispondere</Badge>}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>Aggiornato</th>
                  <th>Motivo</th>
                  <th>Ordine</th>
                  <th>Canale</th>
                  <th>Prodotto</th>
                  <th className="text-end">Importo</th>
                  <th>Cliente</th>
                  <th>Messaggi</th>
                </tr>
              </thead>
              <tbody>
                {righe.map(r => (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setSelezionato(r)}>
                    <td className="small text-nowrap">{dataOra(r.chiuso_at || r.aggiornato_mirakl_at || r.rilevato_at)}</td>
                    <td>{r.motivo || '-'}</td>
                    <td>
                      <div>{r.mirakl_order_id}</div>
                      <div className="text-muted small">{r.data_ordine ? new Date(r.data_ordine).toLocaleDateString('it-IT') : ''}</div>
                    </td>
                    <td>{r.canale}</td>
                    <td>
                      <div>{r.quantita ? `${r.quantita} x ` : ''}{r.prodotto_titolo}</div>
                      <div className="text-muted small">SKU {r.prodotto_sku}</div>
                    </td>
                    <td className="text-end text-nowrap">{euro(r.importo)}</td>
                    <td>{r.cliente}</td>
                    <td className="text-nowrap">
                      {r.n_messaggi} {r.n_allegati > 0 && <span className="text-muted"><FontAwesomeIcon icon={faPaperclip} /> {r.n_allegati}</span>}
                      {r.da_rispondere && stato === 'aperto' && <Badge bg="warning" text="dark" className="ms-2">Da rispondere</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!selezionato} onHide={() => { setSelezionato(null); carica(); }} size="xl" centered fullscreen="lg-down">
        {selezionato && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="fs-5">
                <FontAwesomeIcon icon={faComments} className="me-2" />
                Reclamo ordine {selezionato.mirakl_order_id} — {selezionato.motivo}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="mb-3 small">
                <div><strong>{selezionato.quantita ? `${selezionato.quantita} x ` : ''}{selezionato.prodotto_titolo}</strong> ({euro(selezionato.importo)})</div>
                <div className="text-muted">
                  Cliente: {selezionato.cliente || '-'} · Canale: {selezionato.canale || '-'}
                  {selezionato.ordine_id && <> · <Link to={`/ordini/${selezionato.ordine_id}`}>Apri ordine nel gestionale</Link></>}
                </div>
              </div>
              {selezionato.ordine_id ? (
                <div style={{ height: isMobile ? '65vh' : '55vh' }}>
                  <OrderMessages ordineId={selezionato.ordine_id} />
                </div>
              ) : (
                <Alert variant="warning" className="mb-0">
                  Questo ordine non è presente nel gestionale: i messaggi non sono disponibili qui.
                </Alert>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>
    </Container>
  );
}
